import React, { useState, useEffect } from "react";
import { Spin, Empty, message } from "antd";
import {
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import axios from "../../services/axios";
import moment from "moment";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const user = localStorage.getItem("user");
  const projectName = JSON.parse(localStorage.getItem("Project"));

  const getNotifications = async () => {
    try {
      setLoading(true);
      const res = await axios.post("get-user-notifications", { user, projectName });
      if (res && res.data && res.data.notifications) {
        // Map notifications to include default values for fields not in API response
        const formattedNotifications = res.data.notifications.map((notif) => ({
          ...notif,
          code: notif.code || notif.id || Math.random().toString(), // Fallback for unique key
          type: notif.type || "info", // Default type for icon and border
          isRead: notif.isRead || false, // Default isRead status
          priority: notif.priority || "medium", // Default priority
          createdOn: notif.createdOn || new Date().toISOString(), // Default timestamp
        }));
        setNotifications(formattedNotifications);
      } else {
        message.warning("No notifications found.");
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
      } else if (err.response && err.response.status === 401) {
        message.error("User not authorized");
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false); // Ensure loading is set to false
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  const getNotificationIcon = (type) => {
    const iconMap = {
      info: <InfoCircleOutlined className="text-blue-500" />,
      warning: <ExclamationCircleOutlined className="text-yellow-500" />,
      success: <CheckCircleOutlined className="text-green-500" />,
      error: <CloseCircleOutlined className="text-red-500" />,
    };
    return iconMap[type] || <InfoCircleOutlined className="text-blue-500" />;
  };

  const getNotificationBorderColor = (type) => {
    const colorMap = {
      info: "border-l-blue-500",
      warning: "border-l-yellow-500",
      success: "border-l-green-500",
      error: "border-l-red-500",
    };
    return colorMap[type] || "border-l-blue-500";
  };


  const formatTime = (dateString) => {
    return moment(dateString).format("DD-MM-YYYY hh:mm A"); // Consistent with code 1
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === "unread") return !notification.isRead;
    if (filter === "read") return notification.isRead;
    if (filter !== "all") return notification.type === filter;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Notifications List */}
        <div className="space-y-4">
          {filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-12">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div className="text-center">
                    <p className="text-gray-500 text-lg mb-2">No notifications found</p>
                    <p className="text-gray-400">
                      {filter === "all"
                        ? "You're all caught up! New notifications will appear here."
                        : `No ${filter} notifications at the moment.`}
                    </p>
                  </div>
                }
              />
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.code}
                className={`
                  bg-white rounded-xl shadow-sm border-l-4 transition-all duration-200 hover:shadow-md
                  ${getNotificationBorderColor(notification.type)}
                  ${!notification.isRead ? "bg-blue-50/30" : ""}
                `}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3
                            className={`text-lg font-semibold truncate ${
                              !notification.isRead ? "text-gray-900" : "text-gray-700"
                            }`}
                          >
                            {notification.title}
                          </h3>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                          )}
                        </div>

                        <p className="text-gray-600 mb-3 leading-relaxed">
                          {notification.message}
                        </p>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <FileTextOutlined />
                            <span>Ref: {notification.documentReference}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <CalendarOutlined />
                            <span>{formatTime(notification.createdOn)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Stats */}
        {notifications.length > 0 && (
          <div className="mt-8 p-4 bg-white rounded-xl shadow-sm border">
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{notifications.length}</div>
                <div>Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{unreadCount}</div>
                <div>Unread</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {notifications.filter((n) => n.isRead).length}
                </div>
                <div>Read</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;